<script setup lang="ts">
import LeaderBoardSIdebar from "~/components/Campaign/LeaderBoardSIdebar.vue";
import MasonryWall from "@yeger/vue-masonry-wall";
import { transformToPercentage } from "~/utils/common";
import { formatCompactCurrency } from "~/utils/currencies";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import type { RouterOutput } from "~/plugins/trpc.client";
import Big from "big.js";
import { currencies } from "evangelist-db-schema/currencies";

const { $trpc } = useNuxtApp();
const queryClient = useQueryClient();

type PayoutPostsDto = RouterOutput["listPaychecks"][string];

interface Props {
  paycheck: PayoutPostsDto;
}
const props = defineProps<Props>();

const campaignWeekId = computed(() =>
  props.paycheck.length > 0 ? props.paycheck[0].campaign_week_id : null,
);
const campaignUuid = computed(() =>
  props.paycheck.length > 0 ? props.paycheck[0].campaign_uuid : null,
);
const campaignTitle = computed(() =>
  props.paycheck.length > 0 ? props.paycheck[0].campaign_title : null,
);
const advertiserTheme = computed(() =>
  props.paycheck.length > 0 ? props.paycheck[0].advertiser_theme : null,
);
const rewardCurrency = computed(() =>
  props.paycheck.length > 0 ? props.paycheck[0].reward_currency : null,
);

const averageTweetQualityScore = computed(() => {
  if (props.paycheck.length === 0) return 0;
  const totalQualityScore = props.paycheck.reduce(
    (acc, tweet) => acc + (tweet.quality_score ?? 0),
    0,
  );
  return totalQualityScore / props.paycheck.length;
});

const totalRewards = computed(() =>
  props.paycheck.length > 0
    ? props.paycheck.reduce(
        (acc, tweet) => acc.add(Big(tweet.reward_amount)),
        Big(0),
      )
    : Big(0),
);

const totalRewardsUsd = computed(() => {
  if (props.paycheck.length > 0) return Big(0);
  const conversionRate = Big(props.paycheck[0].usd_conversion_rate);
  return totalRewards.value.mul(conversionRate);
});

const rewarding = ref(false);
const active = ref(true);
const sidebarVisible = ref(false);

const { data: leaderboard } = useQuery({
  enabled: () => campaignWeekId.value !== null,
  queryKey: ["getLeaderboard", campaignWeekId],
  queryFn: () =>
    $trpc.getLeaderboard.query({ campaignWeekId: campaignWeekId.value ?? "" }),
});

const computedMembers = computed(() => {
  return addMemberStyles(leaderboard.value?.entries ?? [], "paycheck");
});

const modalClosedByButton = ref(false);

async function claimAllRewards() {
  rewarding.value = true;

  try {
    modalClosedByButton.value = true;
    await apiService.claimAllRewards(campaignUuid.value ?? "");
    await queryClient.invalidateQueries({ queryKey: ["listPaychecks"] });

    successMessage("Rewards claimed successfully");
    useRouter().push("/");
    globalEmit("refreshBalance");
  } catch (error) {
    errorMessage(error);
  } finally {
    rewarding.value = false;
  }
}

watch(
  () => campaignWeekId.value,
  (newValue) => {
    if (newValue) {
      trackEvent("ui_payday_modal_opened", {
        campaign_id: newValue,
        campaign_title: campaignTitle.value,
      });
    }
  },
  { immediate: true },
);

watch(
  () => active.value,
  (newValue) => {
    if (!newValue) {
      trackEvent("ui_payday_modal_closed", {
        trigger: "button_payday_modal_dismiss_clicked",
        campaign_id: campaignWeekId.value,
        campaign_title: campaignTitle.value,
      });
    }
  },
  { immediate: true },
);
</script>

<template>
  <Modal
    v-if="active && advertiserTheme && campaignTitle"
    class="modal--1024"
    @close="active = false"
  >
    <Confetti />

    <div class="text-center">
      <div class="text-3xl lg:text-4xl font-bold mt-10">It's Payday!</div>
      <div class="my-4 text-lg mt-8">
        Thank you for participating in this campaign.<br
          class="hidden lg:block"
        />
        We are now rewarding your
        <span class="text-purple">top {{ paycheck.length }} posts</span>
        this week.
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 my-10 gap-6 lg:gap-4">
      <div
        class="lg:col-span-2 p-6 bg-gradient-to-b from-white/30 to-gray-200 border border-gray-300 rounded-2xl"
      >
        <div class="flex lg:items-center justify-between gap-6">
          <div>
            <div class="leading-tight lg:leading-snug text-lg font-semibold">
              {{ campaignTitle }}
            </div>
          </div>
          <div class="hidden lg:block">
            <BrandsIconAndName class="font-semibold" :theme="advertiserTheme" />
          </div>
          <div class="block lg:hidden w-8 shrink-0">
            <BrandsIcon class="font-semibold" :theme="advertiserTheme" />
          </div>
        </div>
        <div
          class="mt-4 lg:mt-7 grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-6 rounded-2xl font-semibold cursor-pointer"
        >
          <div class="">
            <div class="flex gap-2 items-end my-4 text-5xl font-bold">
              {{ transformToPercentage(averageTweetQualityScore) }}
            </div>
            <div class="text-black/50">Avg. Post Relevance</div>
          </div>
          <div class="">
            <div
              v-if="rewardCurrency"
              class="flex gap-2 items-end my-4 text-5xl font-bold"
            >
              {{
                Number(totalRewards).toLocaleString(undefined, {
                  maximumFractionDigits: currencies[rewardCurrency].scale,
                })
              }}
              <span class="text-2xl uppercase">{{ rewardCurrency }}</span>
            </div>
            <div v-if="totalRewardsUsd.lt(0)" class="text-black/50">
              Your Total Rewards (~{{
                formatCompactCurrency(totalRewardsUsd.toNumber())
              }})
            </div>
          </div>
        </div>
      </div>
      <div class="bg-purple-light p-6 rounded-2xl">
        <div class="flex items-center mb-5 justify-between">
          <div class="text-black/50">Leaderboard</div>
          <div
            class="hover:underline cursor-pointer"
            @click="sidebarVisible = true"
          >
            See all
          </div>
        </div>
        <CampaignLeaderboard
          :members="leaderboard?.entries ?? []"
          :creator-number="3"
          :campaign-name="campaignTitle"
          :show-title="false"
          :show-footer="false"
          :show-shares="false"
          layout="paycheck"
        />
      </div>
    </div>

    <div class="my-10 text-center">
      <button
        class="btn bg-purple-light hover:bg-black hover:text-white"
        :disabled="rewarding"
        @click="claimAllRewards"
      >
        <LoadingIcon v-if="rewarding" class="icon" />
        <IconsWalletIcon v-else />
        Claim All Rewards
      </button>
    </div>

    <div>
      <MasonryWall
        :items="paycheck"
        :ssr-columns="1"
        :column-width="300"
        :gap="16"
      >
        <template #default="{ item }">
          <TweetPaycheckTweet :post="item" />
        </template>
      </MasonryWall>
    </div>
  </Modal>

  <Sidebar v-if="sidebarVisible" @close="sidebarVisible = false">
    <LeaderBoardSIdebar
      :members="computedMembers"
      :campaignName="campaignTitle ?? ''"
      layout="paycheck"
    />
  </Sidebar>
</template>
